// src/Studio/engine/faceDetection.ts
// RESPONSIBILITY: Face detection and tracking for blur/pixelate effects
// Uses MediaPipe Face Landmarker for detection

import {
  initFaceLandmarker,
  detectInImage,
  detectForVideo,
  disposeFaceLandmarker as disposeMediaPipe,
  isInitialized as isMediaPipeInitialized,
  isLoading as isMediaPipeLoading,
  getLandmarksBoundingBox,
  getLandmarkPosition,
  LANDMARK_INDICES,
  type FaceLandmarkerResult,
} from '../Avatar/faceLandmarker';

// Types for face detection results
export interface FaceBox {
  x: number;
  y: number;
  width: number;
  height: number;
  confidence: number;
}

export interface FaceLandmarks {
  leftEye?: { x: number; y: number };
  rightEye?: { x: number; y: number };
  nose?: { x: number; y: number };
  leftMouth?: { x: number; y: number };
  rightMouth?: { x: number; y: number };
}

export interface DetectedFace {
  id: string;
  box: FaceBox;
  landmarks?: FaceLandmarks;
  trackId?: string; // For tracking across frames
}

export interface FaceDetectionOptions {
  minConfidence: number;
  maxFaces: number;
  inputSize: number;
  scoreThreshold: number;
}

const DEFAULT_OPTIONS: FaceDetectionOptions = {
  minConfidence: 0.5,
  maxFaces: 10,
  inputSize: 416,
  scoreThreshold: 0.5,
};

// Detection state
let isLoading = false;
let loadPromise: Promise<void> | null = null;

// Load MediaPipe Face Landmarker
export async function loadFaceDetector(): Promise<void> {
  if (isMediaPipeInitialized()) return;
  if (loadPromise) return loadPromise;

  isLoading = true;

  loadPromise = (async () => {
    try {
      await initFaceLandmarker();
      console.log('Face detection model loaded (MediaPipe Face Landmarker)');
    } catch (e) {
      console.error('Failed to load MediaPipe Face Landmarker:', e);
      throw new Error('Face detection not available. MediaPipe Face Landmarker failed to load.');
    }
  })();

  try {
    await loadPromise;
  } finally {
    isLoading = false;
  }
}

// Convert MediaPipe result to DetectedFace array
function convertMediaPipeResult(
  result: FaceLandmarkerResult,
  imageWidth: number,
  imageHeight: number,
  options: FaceDetectionOptions
): DetectedFace[] {
  const faces: DetectedFace[] = [];

  if (!result.faceLandmarks || result.faceLandmarks.length === 0) {
    return faces;
  }

  for (let i = 0; i < Math.min(result.faceLandmarks.length, options.maxFaces); i++) {
    const landmarks = result.faceLandmarks[i];

    // Get bounding box from landmarks
    const box = getLandmarksBoundingBox(landmarks, imageWidth, imageHeight);

    // Extract key landmark positions
    const leftEyePos = getLandmarkPosition(
      landmarks,
      LANDMARK_INDICES.leftEyeInner,
      imageWidth,
      imageHeight
    );
    const rightEyePos = getLandmarkPosition(
      landmarks,
      LANDMARK_INDICES.rightEyeInner,
      imageWidth,
      imageHeight
    );
    const nosePos = getLandmarkPosition(
      landmarks,
      LANDMARK_INDICES.noseTip,
      imageWidth,
      imageHeight
    );
    const mouthLeftPos = getLandmarkPosition(
      landmarks,
      LANDMARK_INDICES.mouthLeft,
      imageWidth,
      imageHeight
    );
    const mouthRightPos = getLandmarkPosition(
      landmarks,
      LANDMARK_INDICES.mouthRight,
      imageWidth,
      imageHeight
    );

    // MediaPipe doesn't provide per-face confidence, so we use a default high value
    // since detection is generally reliable when landmarks are found
    const confidence = 0.95;

    if (confidence >= options.minConfidence) {
      faces.push({
        id: `face-${i}-${Date.now()}`,
        box: {
          ...box,
          confidence,
        },
        landmarks: {
          leftEye: leftEyePos ? { x: leftEyePos.x, y: leftEyePos.y } : undefined,
          rightEye: rightEyePos ? { x: rightEyePos.x, y: rightEyePos.y } : undefined,
          nose: nosePos ? { x: nosePos.x, y: nosePos.y } : undefined,
          leftMouth: mouthLeftPos ? { x: mouthLeftPos.x, y: mouthLeftPos.y } : undefined,
          rightMouth: mouthRightPos ? { x: mouthRightPos.x, y: mouthRightPos.y } : undefined,
        },
      });
    }
  }

  return faces;
}

// Detect faces in an image or video frame
export async function detectFaces(
  source: HTMLImageElement | HTMLVideoElement | HTMLCanvasElement,
  options: Partial<FaceDetectionOptions> = {}
): Promise<DetectedFace[]> {
  const opts = { ...DEFAULT_OPTIONS, ...options };

  if (!isMediaPipeInitialized()) {
    await loadFaceDetector();
  }

  let result: FaceLandmarkerResult | null = null;
  try {
    let width: number;
    let height: number;

    if (source instanceof HTMLVideoElement) {
      // For video, use detectForVideo with current timestamp
      width = source.videoWidth;
      height = source.videoHeight;
      const timestamp = performance.now();
      result = await detectForVideo(source, timestamp);
    } else {
      // For images and canvas, use detectInImage
      width = source instanceof HTMLImageElement ? source.naturalWidth : source.width;
      height = source instanceof HTMLImageElement ? source.naturalHeight : source.height;
      result = await detectInImage(source as HTMLImageElement | HTMLCanvasElement);
    }

    return convertMediaPipeResult(result, width, height, opts);
  } catch (e) {
    console.error('Face detection error:', e);
    return [];
  } finally {
    try {
      (result as any)?.close?.();
    } catch {}
  }
}

// Simple face tracker using IOU overlap
export class FaceTracker {
  private tracks: Map<string, DetectedFace[]> = new Map();
  private nextTrackId = 1;
  private maxAge = 10; // Frames to keep track alive without detection

  update(faces: DetectedFace[]): DetectedFace[] {
    const assigned = new Set<string>();
    const result: DetectedFace[] = [];

    // Match new faces to existing tracks
    for (const face of faces) {
      let bestTrackId: string | null = null;
      let bestIOU = 0.3; // Minimum IOU threshold

      for (const [trackId, history] of this.tracks) {
        if (assigned.has(trackId)) continue;

        const lastFace = history[history.length - 1];
        const iou = calculateIOU(face.box, lastFace.box);

        if (iou > bestIOU) {
          bestIOU = iou;
          bestTrackId = trackId;
        }
      }

      if (bestTrackId) {
        // Update existing track
        assigned.add(bestTrackId);
        const history = this.tracks.get(bestTrackId)!;
        face.trackId = bestTrackId;
        history.push(face);

        // Limit history length
        if (history.length > 30) {
          history.shift();
        }
      } else {
        // Create new track
        const trackId = `track-${this.nextTrackId++}`;
        face.trackId = trackId;
        this.tracks.set(trackId, [face]);
      }

      result.push(face);
    }

    // Age out unmatched tracks
    for (const [trackId, history] of this.tracks) {
      if (!assigned.has(trackId)) {
        // Add "empty" frame to history
        if (history.length > this.maxAge) {
          this.tracks.delete(trackId);
        }
      }
    }

    return result;
  }

  getSmoothedPosition(trackId: string, frames: number = 5): FaceBox | null {
    const history = this.tracks.get(trackId);
    if (!history || history.length === 0) return null;

    const recent = history.slice(-frames);
    const avg = {
      x: 0,
      y: 0,
      width: 0,
      height: 0,
      confidence: 0,
    };

    for (const face of recent) {
      avg.x += face.box.x;
      avg.y += face.box.y;
      avg.width += face.box.width;
      avg.height += face.box.height;
      avg.confidence += face.box.confidence;
    }

    const n = recent.length;
    return {
      x: avg.x / n,
      y: avg.y / n,
      width: avg.width / n,
      height: avg.height / n,
      confidence: avg.confidence / n,
    };
  }

  reset(): void {
    this.tracks.clear();
    this.nextTrackId = 1;
  }
}

// Calculate Intersection over Union
function calculateIOU(a: FaceBox, b: FaceBox): number {
  const x1 = Math.max(a.x, b.x);
  const y1 = Math.max(a.y, b.y);
  const x2 = Math.min(a.x + a.width, b.x + b.width);
  const y2 = Math.min(a.y + a.height, b.y + b.height);

  const intersection = Math.max(0, x2 - x1) * Math.max(0, y2 - y1);
  const areaA = a.width * a.height;
  const areaB = b.width * b.height;
  const union = areaA + areaB - intersection;

  return intersection / union;
}

// Apply blur effect to detected faces
export function applyFaceBlur(
  ctx: CanvasRenderingContext2D,
  faces: DetectedFace[],
  blurRadius: number = 20,
  padding: number = 10
): void {
  for (const face of faces) {
    const { x, y, width, height } = face.box;

    // Add padding
    const px = x - padding;
    const py = y - padding;
    const pw = width + padding * 2;
    const ph = height + padding * 2;

    // Get face region
    const faceData = ctx.getImageData(
      Math.max(0, px),
      Math.max(0, py),
      Math.min(pw, ctx.canvas.width - px),
      Math.min(ph, ctx.canvas.height - py)
    );

    // Apply box blur (simplified)
    const blurred = boxBlur(faceData, blurRadius);

    // Draw back
    ctx.putImageData(
      blurred,
      Math.max(0, px),
      Math.max(0, py)
    );
  }
}

// Apply pixelate effect to detected faces
export function applyFacePixelate(
  ctx: CanvasRenderingContext2D,
  faces: DetectedFace[],
  pixelSize: number = 10,
  padding: number = 10
): void {
  for (const face of faces) {
    const { x, y, width, height } = face.box;

    // Add padding
    const px = Math.max(0, x - padding);
    const py = Math.max(0, y - padding);
    const pw = Math.min(width + padding * 2, ctx.canvas.width - px);
    const ph = Math.min(height + padding * 2, ctx.canvas.height - py);

    // Create small canvas
    const tempCanvas = document.createElement('canvas');
    const tempCtx = tempCanvas.getContext('2d')!;

    // Scale down
    const sw = Math.ceil(pw / pixelSize);
    const sh = Math.ceil(ph / pixelSize);
    tempCanvas.width = sw;
    tempCanvas.height = sh;

    // Draw scaled down
    tempCtx.drawImage(ctx.canvas, px, py, pw, ph, 0, 0, sw, sh);

    // Draw scaled up (pixelated)
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(tempCanvas, 0, 0, sw, sh, px, py, pw, ph);
    ctx.imageSmoothingEnabled = true;
  }
}

// Simple box blur implementation
function boxBlur(imageData: ImageData, radius: number): ImageData {
  const { data, width, height } = imageData;
  const output = new ImageData(width, height);
  const outputData = output.data;

  radius = Math.min(radius, Math.min(width, height) / 2);

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      let r = 0, g = 0, b = 0, a = 0;
      let count = 0;

      for (let dy = -radius; dy <= radius; dy++) {
        for (let dx = -radius; dx <= radius; dx++) {
          const nx = x + dx;
          const ny = y + dy;

          if (nx >= 0 && nx < width && ny >= 0 && ny < height) {
            const i = (ny * width + nx) * 4;
            r += data[i];
            g += data[i + 1];
            b += data[i + 2];
            a += data[i + 3];
            count++;
          }
        }
      }

      const i = (y * width + x) * 4;
      outputData[i] = r / count;
      outputData[i + 1] = g / count;
      outputData[i + 2] = b / count;
      outputData[i + 3] = a / count;
    }
  }

  return output;
}

// Check if face detection is available
export function isFaceDetectionAvailable(): boolean {
  return isMediaPipeInitialized();
}

// Check if face detection is loading
export function isFaceDetectionLoading(): boolean {
  return isLoading || isMediaPipeLoading();
}

// Cleanup
export function disposeFaceDetector(): void {
  disposeMediaPipe();
  loadPromise = null;
}
